import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Star, Undo2 } from 'lucide-react';
import {
  AppShell,
  AppShellBody,
  AppMain,
  ContextualSidebar,
  AppPanel,
  useAppShell,
  type ContextualNavSection,
  type ContextualNavEntry,
} from '@vnetwork/ui/layout';
import { Tooltip } from '@vnetwork/ui/primitives';
import { useAuth } from '@/auth/AuthProvider';
import { SERVICE_CATEGORIES, SERVICE_GROUPS, brandServiceCategories } from '@/lib/service-categories';
import { useServicesNavTree } from './services-nav-context';
import { PinnedServicesProvider, usePinnedLeaves } from './pinned-services';
import { PRIMARY_NAV, isActive } from './nav';
import { NavProvider, useContextualNav } from '@vnetwork/margarita-shell/nav-context';
import { MAIN_CONTENT_ID, SKIP_LINK_LABEL, composeTitleParts, navFresh, navTitleParts, settleTitleParts } from '@vnetwork/margarita-shell/page-title';
import { useDocumentTitle } from '@vnetwork/margarita-shell/useDocumentTitle';
import { usePageTitle } from '@vnetwork/margarita-shell/usePageTitle';
import { PageTitleLeafProvider, usePageTitleLeafValue } from '@vnetwork/margarita-shell/usePageTitleLeaf';
import { AppTopbar } from './AppTopbar';
import { WorkingProjectSync } from './WorkingProjectSync';
import { HomeOrgNote, TabChangedNotice, useWorkspaceDocumentTitle } from './WorkspaceHints';
import { ContextStrip } from './project-switcher/ContextStrip';
import {
  OverviewIcon,
  AllServicesIcon,
  SecurityIcon,
  KubernetesIcon,
  ManagementIcon,
  AccountIcon,
  SidebarToggleIcon,
  SearchIcon,
  ComputeIcon,
  DatabaseIcon,
  StorageIcon,
  StreamingIcon,
  GlobeIcon,
  ObservabilityIcon,
} from './shell-icons';
import { CommandMenu } from './CommandMenu';
import { ScanReportCard } from '@/components/scan-report/ScanReportCard';
// NOTE: the live-chat assistant (AskAiPanel + useAskAi) is intentionally kept in
// the tree but no longer mounted — the topbar button now opens the Documentation
// panel instead. Restore the button in AppTopbar to bring the assistant back.
import { DocsPanel } from './DocsPanel';
import { LiveChatWidget } from '@/components/livechat/LiveChatWidget';
import { brand } from '@/config/brand';
import styles from './AppLayout.module.css';

/** True while the sidebar is the icon-only 56px rail. */
function useRailCollapsed() {
  return useAppShell().collapsed;
}

/**
 * Icon-only rail: the label is gone, so name the icon in a tooltip. The ::after in
 * the module stretches the hover target over the whole 32px row, not just the 16px glyph.
 */
function RailTip({ label, children }: { label: string; children: ReactNode }) {
  const rail = useRailCollapsed();
  if (!rail) return <>{children}</>;
  return (
    <Tooltip content={label} side="right">
      <span className={styles.railTip}>{children}</span>
    </Tooltip>
  );
}

const withRailTip = (label: ReactNode, icon: ReactNode): ReactNode =>
  icon && typeof label === 'string' ? <RailTip label={label}>{icon}</RailTip> : icon;

/**
 * Icon-only rail: a group can't show its children, so it becomes a plain icon that
 * expands the sidebar (and opens that group) on click. A child's active state is
 * carried by its parent icon.
 */
function toRailSections(
  sections: ContextualNavSection[],
  onExpandGroup: (id: string) => void,
): ContextualNavSection[] {
  return sections.map((section) => ({
    ...section,
    items: section.items.map((entry) =>
      'items' in entry
        ? {
            id: entry.id,
            label: entry.label,
            icon: withRailTip(entry.label, entry.icon),
            onSelect: () => onExpandGroup(entry.id),
          }
        : { ...entry, icon: withRailTip(entry.label, entry.icon) },
    ),
  }));
}

function toFullSections(sections: ContextualNavSection[], openGroupId: string | null): ContextualNavSection[] {
  return sections.map((section) => ({
    ...section,
    items: section.items.map((entry) =>
      'items' in entry
        ? { ...entry, defaultOpen: entry.defaultOpen || entry.id === openGroupId }
        : entry,
    ),
  }));
}

const parentOfActive = (sections: ContextualNavSection[], activeId?: string): string | undefined => {
  for (const section of sections) {
    for (const entry of section.items) {
      if ('items' in entry && entry.items.some((leaf) => leaf.id === activeId)) return entry.id;
    }
  }
  return activeId;
};

function SidebarFooterRow({ pathname }: { pathname: string }) {
  const { collapsed, toggle } = useAppShell();
  const rail = useRailCollapsed();
  const inAccount = pathname === '/account' || pathname.startsWith('/account/');
  return (
    <div className={styles.footerRow}>
      <Tooltip content={rail ? 'Account' : undefined} side="right">
        <Link
          to="/account"
          className={styles.accountLink}
          aria-label="Account"
          aria-current={inAccount ? 'page' : undefined}
        >
          <AccountIcon size={16} />
          <span className={styles.accountLabel}>Account</span>
        </Link>
      </Tooltip>
      <Tooltip content={rail ? 'Expand sidebar' : undefined} side="right">
        <button
          type="button"
          className={styles.collapseBtn}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          onClick={toggle}
        >
          <SidebarToggleIcon size={16} />
        </button>
      </Tooltip>
    </div>
  );
}

const SERVICE_HEADERS: Record<string, { title: string; icon: string }> = {
  waap: { title: 'Web Application & API Protection', icon: '/assets/services/svc/waap.svg' },
};

const CATEGORY_ICONS: Record<string, (props: { size?: number }) => React.ReactElement> = {
  security: SecurityIcon,
  compute: ComputeIcon,
  kubernetes: KubernetesIcon,
  databases: DatabaseIcon,
  storage: StorageIcon,
  streaming: StreamingIcon,
  cdn: GlobeIcon,
  observability: ObservabilityIcon,
  management: ManagementIcon,
};

/** Service identity block under "Back to …" in a service console (icon, name, org id). */
function ServiceHeader({ pathname }: { pathname: string }) {
  const { user } = useAuth();
  const slug = pathname.match(/^\/services\/([^/]+)\/[^/]+/)?.[1];
  const meta = slug ? SERVICE_HEADERS[slug] : undefined;
  if (!meta) return null;
  return (
    <div className={styles.serviceHeader}>
      <img src={meta.icon} alt="" className={styles.serviceIcon} />
      <div className={styles.serviceText}>
        <div className={styles.serviceTitle}>{meta.title}</div>
        {user?.organizationId ? <div className={styles.serviceSub}>ID {user.organizationId}</div> : null}
      </div>
    </div>
  );
}

/**
 * Full-width strip shown while a DataCentral employee is impersonating this
 * customer, so it's obvious the session is staff-driven (not the customer's own
 * login) and who is behind it. Hidden for a direct customer login.
 */
function ImpersonationBanner() {
  const { isImpersonating, impersonatedBy } = useAuth();
  if (!isImpersonating || !impersonatedBy) return null;
  const who = impersonatedBy.name || impersonatedBy.email || `#${impersonatedBy.id}`;
  return (
    <div
      role="status"
      style={{
        padding: '8px 16px',
        background: 'var(--vn-warning-subtle)',
        borderBottom: '1px solid var(--vn-warning-border)',
        color: 'var(--vn-warning-text)',
        fontSize: 13,
        fontWeight: 600,
        textAlign: 'center',
      }}
    >
      DataCentral staff session — you are impersonating this account as{' '}
      <strong>{who}</strong>
      {impersonatedBy.email && impersonatedBy.name ? ` (${impersonatedBy.email})` : ''}. Actions
      are performed on the customer&apos;s account.
    </div>
  );
}

export function AppLayout() {
  const { pathname } = useLocation();
  return (
    <NavProvider>
      <PageTitleLeafProvider pathname={pathname}>
        <PinnedServicesProvider>
          <AppLayoutInner />
        </PinnedServicesProvider>
      </PageTitleLeafProvider>
    </NavProvider>
  );
}

function AppLayoutInner() {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const [cmdkOpen, setCmdkOpen] = useState(false);
  const [docsOpen, setDocsOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [railCollapsed, setRailCollapsed] = useState(false);
  // Group the user expanded the rail for — opens on the next full render.
  const [openGroupId, setOpenGroupId] = useState<string | null>(null);

  // Close mobile nav on route change.
  useEffect(() => {
    setMobileNavOpen(false);
  }, [pathname]);

  // A nested service console may register its own scope (sections + "Back to …").
  // When none is registered, fall back to the primary app nav.
  const scoped = useContextualNav();

  // The user's pinned services — surfaced as a top sidebar section (primary nav
  // only) and on the dashboard.
  const pinnedLeaves = usePinnedLeaves();

  // Provisioned services grouped by category (CDN collapsed to one umbrella
  // per org) — drives both the count badges and the click-through targets.
  const servicesTree = useServicesNavTree();
  const servicesByCategory = useMemo(
    () => new Map(servicesTree.map((cat) => [cat.key, cat.services])),
    [servicesTree],
  );

  // Cloudflare-style grouped nav: catalog categories are pulled OUT of a single
  // "Services" parent and clustered under uppercase group headings (Protect /
  // Infrastructure / Delivery / Observe & Manage, per SERVICE_GROUPS). Leaf ids stay
  // `services:<key>` / `services:all` so active-highlighting is unchanged.
  const primarySections = useMemo<ContextualNavSection[]>(() => {
    const enabledCats = brandServiceCategories();
    const catByKey = new Map(enabledCats.map((c) => [c.key, c]));
    const navLeaf = (item: (typeof PRIMARY_NAV)[number]): ContextualNavEntry => {
      const Icon = item.to === '/' ? OverviewIcon : item.icon;
      return { id: item.to, label: item.label, icon: <Icon size={16} />, onSelect: () => navigate(item.to) };
    };
    // A category leaf: one provisioned service deep-links into its console; several
    // (or none) open the dashboard filtered to the category.
    const categoryLeaf = (cat: (typeof enabledCats)[number]): ContextualNavEntry => {
      const CatIcon = CATEGORY_ICONS[cat.key] ?? cat.icon;
      const svcs = servicesByCategory.get(cat.key) ?? [];
      // A styled ReactNode bypasses @ui's hardcoded blue `tone="primary"` Badge:
      // "Popular" uses brand-red.
      const badgeNode: React.ReactNode = cat.badge
        ? <span className={styles.popularBadge}>{cat.badge}</span>
        : undefined;
      return {
        id: `services:${cat.key}`,
        label: cat.title,
        icon: <CatIcon size={16} />,
        badge: badgeNode,
        // A category with a dedicated landing (e.g. compute, security) opens that
        // hub. Otherwise: one provisioned service deep-links into its console;
        // several (or none) open the dashboard filtered to the category.
        onSelect: () =>
          cat.landingRoute
            ? navigate(cat.landingRoute)
            : navigate(svcs.length === 1 ? svcs[0].route : `/services?category=${cat.key}`),
      };
    };
    const overview = PRIMARY_NAV.find((i) => i.to === '/');
    return [
      // Ungrouped top: Overview + the all-services dashboard.
      {
        items: [
          ...(overview ? [navLeaf(overview)] : []),
          {
            id: 'services:all',
            label: 'All services',
            icon: <AllServicesIcon size={16} />,
            onSelect: () => navigate('/services'),
          },
        ],
      },
      // One section per top-level group — categories under an uppercase heading.
      // Groups with no brand-enabled category are omitted entirely (no empty headings).
      ...SERVICE_GROUPS.flatMap((g) => {
        const items = g.categoryKeys
          .map((k) => catByKey.get(k))
          .filter((c): c is (typeof enabledCats)[number] => !!c)
          .map(categoryLeaf);
        return items.length > 0 ? [{ heading: g.heading, items }] : [];
      }),
      // Support is reachable from the topbar action only — no duplicate sidebar
      // entry (both went to the same /support ticket page).
    ];
  }, [navigate, servicesByCategory]);

  const activePrimary = PRIMARY_NAV.find((item) => isActive(item, pathname))?.to;
  // Inside /services, highlight the nested category leaf instead of the parent.
  // A category leaf is also active when the current path is inside the console
  // of its single service (the deep-link case). Consoles that register their
  // own scoped nav bypass this entirely.
  const servicesCategory = new URLSearchParams(search).get('category');
  // A category whose dedicated landing (or a console beneath it) is open.
  const landedCategory = SERVICE_CATEGORIES.find(
    (c) =>
      c.landingRoute &&
      (pathname === c.landingRoute || pathname.startsWith(`${c.landingRoute}/`)),
  )?.key;
  const deepLinkedCategory = servicesTree.find(
    (cat) =>
      cat.services.length === 1 &&
      (pathname === cat.services[0].route ||
        pathname.startsWith(`${cat.services[0].route}/`)),
  )?.key;
  const primaryActiveId =
    activePrimary === '/services'
      ? landedCategory
        ? `services:${landedCategory}`
        : deepLinkedCategory
        ? `services:${deepLinkedCategory}`
        : pathname === '/services' &&
            servicesCategory &&
            SERVICE_CATEGORIES.some((c) => c.key === servicesCategory)
          ? `services:${servicesCategory}`
          : 'services:all'
      : activePrimary;

  const searchHeader = (
    <button type="button" className={styles.searchTrigger} onClick={() => setCmdkOpen(true)}>
      <SearchIcon size={14} />
      <span className={styles.searchLabel}>Quick search…</span>
      <kbd className={styles.searchKbd}>⌘K</kbd>
    </button>
  );

  // Account lives in the sidebar footer (next to the collapse toggle); inside the
  // account scope the sidebar shows its own sub-nav instead of the primary nav.
  const inAccountScope = pathname === '/account' || pathname.startsWith('/account/');
  const baseSections = scoped?.sections ?? primarySections;

  // "Pinned" shortcuts sit at the top of the PRIMARY nav (not inside a console's
  // own scoped nav, which stays focused on that service).
  const pinnedSection: ContextualNavSection | null =
    !scoped && !inAccountScope && pinnedLeaves.length > 0
      ? {
          heading: 'Pinned',
          items: pinnedLeaves.map((s) => ({
            id: `pinned:${s.id}`,
            label: s.label,
            icon: <Star size={16} />,
            onSelect: () => navigate(s.route),
          })),
        }
      : null;

  const sections: ContextualNavSection[] = inAccountScope
    ? baseSections
    : [...(pinnedSection ? [pinnedSection] : []), ...baseSections];

  const closeMobileNav = () => setMobileNavOpen(false);

  const activeNavId = scoped ? scoped.activeId : primaryActiveId;
  // Every page gets its title from the active nav entry; another organization's workspace keeps the title it sets itself.
  const workspaceTitle = useWorkspaceDocumentTitle();
  useDocumentTitle(workspaceTitle);
  const pageLeaf = usePageTitleLeafValue();
  // The nav is registered one commit after the route changes: until it names the current route the last settled title stays.
  const titleParts = composeTitleParts(navTitleParts(sections, activeNavId), pageLeaf, scoped?.title);
  const [settledParts, setSettledParts] = useState(titleParts);
  const titleFresh = navFresh(pathname, scoped?.pathname);
  if (titleFresh && settledParts.join('\0') !== titleParts.join('\0')) setSettledParts(titleParts);
  usePageTitle(workspaceTitle ? null : settleTitleParts(settledParts, titleParts, titleFresh), brand.appName);
  const navSections = railCollapsed
    ? toRailSections(sections, (id) => {
        setOpenGroupId(id);
        setRailCollapsed(false);
      })
    : toFullSections(sections, openGroupId);
  const navActiveId = railCollapsed ? parentOfActive(sections, activeNavId) : activeNavId;

  return (
    <AppShell
      collapsed={railCollapsed}
      onCollapsedChange={(next) => {
        if (next) setOpenGroupId(null);
        setRailCollapsed(next);
      }}
    >
      <a
        href={`#${MAIN_CONTENT_ID}`}
        className={styles.skipLink}
        onClick={(e) => {
          e.preventDefault(); // a hash change would be a history entry the router sees
          document.getElementById(MAIN_CONTENT_ID)?.focus();
        }}
      >
        {SKIP_LINK_LABEL}
      </a>
      <WorkingProjectSync />
      <AppTopbar
        mobileNavOpen={mobileNavOpen}
        onToggleMobileNav={() => setMobileNavOpen((o) => !o)}
        docsOpen={docsOpen}
        onToggleDocs={() => setDocsOpen((o) => !o)}
      />
      <CommandMenu open={cmdkOpen} onOpenChange={setCmdkOpen} />
      <AppShellBody>
        {/* Mobile drawer backdrop — only rendered/visible on mobile when nav is open */}
        {mobileNavOpen && (
          <div
            className={styles.mobileBackdrop}
            aria-hidden="true"
            onClick={closeMobileNav}
          />
        )}

        <ContextualSidebar
          className={[
            styles.sidebar,
            scoped ? styles.sidebarScoped : undefined,
            mobileNavOpen ? styles.sidebarOpen : undefined,
          ]
            .filter(Boolean)
            .join(' ')}
          aria-label={scoped ? 'Service' : 'Primary'}
          sections={navSections}
          activeId={navActiveId}
          back={scoped?.back ? { ...scoped.back, icon: scoped.back.icon ?? <Undo2 size={15} /> } : undefined}
          header={
            <>
              {searchHeader}
              {scoped ? scoped.header ?? <ServiceHeader pathname={pathname} /> : null}
            </>
          }
          footer={
            <div className={styles.footerStack}>
              <ScanReportCard />
              <SidebarFooterRow pathname={pathname} />
            </div>
          }
          onItemSelect={closeMobileNav}
        />
        <AppMain id={MAIN_CONTENT_ID} tabIndex={-1} className={styles.main}>
          <ImpersonationBanner />
          <ContextStrip />
          <TabChangedNotice />
          <HomeOrgNote />
          <Outlet />
        </AppMain>
        {docsOpen ? (
          <AppPanel aria-label="Documentation" width="440px">
            <DocsPanel onClose={() => setDocsOpen(false)} />
          </AppPanel>
        ) : null}
      </AppShellBody>
      {brand.features.livechat && <LiveChatWidget />}
    </AppShell>
  );
}
